import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Play, Pause, Square, Volume2, Gauge, ChevronDown, Mic } from 'lucide-react';

/**
 * LessonAudioPlayer
 * ---------------------------------------------------------------
 * Dual-mode audio player:
 *  1. If `audioUrl` is provided → plays the admin-uploaded MP3 via HTML5 <audio>.
 *  2. Otherwise → falls back to free browser-native Web Speech API TTS.
 *
 * Props:
 *   - text:      string (required) — text to be spoken via TTS fallback
 *   - title:     string (optional) — small label shown on player
 *   - audioUrl:  string (optional) — backend URL to admin-uploaded MP3
 */
export default function LessonAudioPlayer({ text, title = 'Listen to this lesson', audioUrl }) {
  if (audioUrl) {
    return <UploadedAudioPlayer audioUrl={audioUrl} title={title} />;
  }
  return <TtsAudioPlayer text={text} title={title} />;
}


/* =================== UPLOADED MP3 PLAYER =================== */
function UploadedAudioPlayer({ audioUrl, title }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [rate, setRate] = useState(1);
  const [duration, setDuration] = useState(0);
  const [showSettings, setShowSettings] = useState(false);

  // Resolve relative paths against the backend URL.
  const src = audioUrl?.startsWith('http')
    ? audioUrl
    : `${process.env.REACT_APP_BACKEND_URL || ''}${audioUrl}`;

  useEffect(() => {
    const el = audioRef.current; if (!el) return;
    const onTime = () => setProgress(el.duration ? el.currentTime / el.duration : 0);
    const onMeta = () => setDuration(el.duration || 0);
    const onEnd = () => { setPlaying(false); setProgress(1); };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('ended', onEnd);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('ended', onEnd);
    };
  }, []);

  const togglePlay = () => {
    const el = audioRef.current; if (!el) return;
    if (playing) { el.pause(); setPlaying(false); }
    else { el.play().then(() => setPlaying(true)).catch(() => {}); }
  };
  const stop = () => {
    const el = audioRef.current; if (!el) return;
    el.pause(); el.currentTime = 0; setPlaying(false); setProgress(0);
  };
  const changeRate = (r) => {
    setRate(r);
    if (audioRef.current) audioRef.current.playbackRate = r;
  };

  const pct = Math.round(progress * 100);
  const fmt = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div className="bg-ink text-white rounded-[var(--kc-radius-md)] shadow-kc overflow-hidden" data-testid="audio-player-uploaded">
      <audio ref={audioRef} src={src} preload="metadata"/>
      <div className="px-4 py-3 flex items-center gap-3">
        <button
          onClick={togglePlay}
          className="h-11 w-11 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-[color:var(--kc-gold)] text-black hover:bg-[color:var(--kc-gold-2)] transition-colors"
          aria-label={playing ? 'Pause' : 'Play'}
          data-testid="audio-mp3-toggle"
        >
          {playing ? <Pause size={18} fill="currentColor"/> : <Play size={18} fill="currentColor"/>}
        </button>
        <button
          onClick={stop}
          disabled={!playing && progress === 0}
          className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/20 text-white/80 hover:bg-white/10 disabled:opacity-40"
          aria-label="Stop"
          data-testid="audio-mp3-stop"
        >
          <Square size={14} fill="currentColor"/>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Mic size={12} className="text-gold flex-shrink-0"/>
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 truncate" data-testid="audio-mp3-title">
              {title} <span className="text-[color:var(--kc-gold)]">· Mickey</span>
            </div>
            <div className="ml-auto text-[11px] tabular-nums text-white/60">
              {fmt(audioRef.current?.currentTime || 0)} / {fmt(duration)}
            </div>
          </div>
          <div className="mt-1.5 h-1 w-full bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-[color:var(--kc-gold)] transition-[width] duration-300" style={{ width: `${pct}%` }} data-testid="audio-mp3-progress"/>
          </div>
        </div>
        <button
          onClick={() => setShowSettings((s) => !s)}
          className="h-9 px-2.5 inline-flex items-center gap-1 rounded-full border border-white/20 text-white/80 hover:bg-white/10 text-[11px] font-semibold"
          data-testid="audio-mp3-settings"
        >
          <Gauge size={12}/> {rate.toFixed(2)}x
          <ChevronDown size={12} className={`transition-transform ${showSettings ? 'rotate-180' : ''}`}/>
        </button>
      </div>
      {showSettings && (
        <div className="border-t border-white/10 px-4 py-3 bg-black/40">
          <label className="block text-[10px] uppercase tracking-[0.18em] text-white/60 mb-1">Speed</label>
          <div className="flex flex-wrap gap-1.5">
            {[0.75, 1, 1.25, 1.5, 1.75, 2].map(r => (
              <button key={r} onClick={() => changeRate(r)} className={`h-7 px-2.5 text-[11px] rounded-full border ${Math.abs(rate - r) < 0.01 ? 'bg-[color:var(--kc-gold)] text-black border-[color:var(--kc-gold)] font-semibold' : 'border-white/20 text-white/80 hover:bg-white/10'}`}>
                {r}x
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* =================== TTS FALLBACK PLAYER =================== */
function TtsAudioPlayer({ text, title }) {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState('');
  const [rate, setRate] = useState(1);
  const [status, setStatus] = useState('idle'); // idle | playing | paused
  const [progress, setProgress] = useState(0);  // 0..1
  const [showSettings, setShowSettings] = useState(false);

  const chunksRef = useRef([]);
  const chunkIdxRef = useRef(0);
  const totalCharsRef = useRef(0);
  const consumedCharsRef = useRef(0);

  // Load voices (async in some browsers)
  useEffect(() => {
    if (!supported) return;
    const load = () => {
      const v = window.speechSynthesis.getVoices();
      if (!v || v.length === 0) return;
      // Prefer high-quality English voices
      const ranked = [...v].sort((a, b) => {
        const score = (vo) => {
          let s = 0;
          if (/en[-_]US|en[-_]GB|^en$/i.test(vo.lang)) s += 5;
          if (/Samantha|Daniel|Karen|Google US English|Microsoft (Aria|Guy|Jenny)|Alex|Fred/i.test(vo.name)) s += 4;
          if (vo.localService) s += 1;
          return s;
        };
        return score(b) - score(a);
      });
      setVoices(ranked);
      setVoiceURI((prev) => prev || ranked[0]?.voiceURI || '');
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, [supported]);

  // Clean up on unmount or text change
  useEffect(() => {
    return () => { if (supported) window.speechSynthesis.cancel(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the text changes, stop any current playback
  useEffect(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setStatus('idle');
    setProgress(0);
    chunksRef.current = [];
    chunkIdxRef.current = 0;
    consumedCharsRef.current = 0;
  }, [text, supported]);

  const buildChunks = useCallback((raw) => {
    // Split into sentences then group up to ~200 chars per chunk to keep speech engines happy
    const sentences = raw.replace(/\s+/g, ' ').match(/[^.!?]+[.!?]+|\S+$/g) || [raw];
    const chunks = [];
    let buf = '';
    for (const s of sentences) {
      if ((buf + ' ' + s).trim().length > 200 && buf) {
        chunks.push(buf.trim());
        buf = s;
      } else {
        buf = (buf + ' ' + s).trim();
      }
    }
    if (buf) chunks.push(buf.trim());
    return chunks;
  }, []);

  const speakChunk = useCallback((i) => {
    const chunks = chunksRef.current;
    if (i >= chunks.length) {
      setStatus('idle');
      setProgress(1);
      return;
    }
    const u = new SpeechSynthesisUtterance(chunks[i]);
    const v = voices.find((x) => x.voiceURI === voiceURI);
    if (v) u.voice = v;
    u.rate = rate;
    u.pitch = 1;
    u.volume = 1;
    u.onend = () => {
      consumedCharsRef.current += chunks[i].length;
      const p = Math.min(1, consumedCharsRef.current / Math.max(1, totalCharsRef.current));
      setProgress(p);
      chunkIdxRef.current = i + 1;
      // continue if still playing (not cancelled)
      if (window.speechSynthesis.speaking || window.speechSynthesis.pending || status === 'playing') {
        speakChunk(i + 1);
      } else if (i + 1 < chunks.length) {
        speakChunk(i + 1);
      }
    };
    u.onerror = () => { /* ignore — likely cancelled */ };
    window.speechSynthesis.speak(u);
  }, [voices, voiceURI, rate, status]);

  const handlePlay = () => {
    if (!supported || !text) return;
    if (status === 'paused') {
      window.speechSynthesis.resume();
      setStatus('playing');
      return;
    }
    // fresh start
    window.speechSynthesis.cancel();
    const chunks = buildChunks(text);
    chunksRef.current = chunks;
    chunkIdxRef.current = 0;
    consumedCharsRef.current = 0;
    totalCharsRef.current = chunks.reduce((acc, c) => acc + c.length, 0);
    setProgress(0);
    setStatus('playing');
    speakChunk(0);
  };

  const handlePause = () => {
    if (!supported) return;
    window.speechSynthesis.pause();
    setStatus('paused');
  };

  const handleStop = () => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setStatus('idle');
    setProgress(0);
    chunkIdxRef.current = 0;
    consumedCharsRef.current = 0;
  };

  // When rate or voice changes mid-playback, restart from current chunk
  const handleRateChange = (newRate) => {
    setRate(newRate);
    if (status === 'playing' || status === 'paused') {
      const resumeFrom = chunkIdxRef.current;
      window.speechSynthesis.cancel();
      // small timeout to ensure cancel completes
      setTimeout(() => {
        setStatus('playing');
        speakChunk(resumeFrom);
      }, 60);
    }
  };

  const handleVoiceChange = (uri) => {
    setVoiceURI(uri);
    if (status === 'playing' || status === 'paused') {
      const resumeFrom = chunkIdxRef.current;
      window.speechSynthesis.cancel();
      setTimeout(() => {
        setStatus('playing');
        speakChunk(resumeFrom);
      }, 60);
    }
  };

  if (!supported) {
    return (
      <div className="bg-surface-2 border border-kc rounded-[var(--kc-radius-md)] p-4 text-sm text-muted-kc" data-testid="audio-unsupported">
        Audio narration isn't supported in this browser. Try Chrome, Safari, or Edge for the listen-along feature.
      </div>
    );
  }

  const pct = Math.round(progress * 100);

  return (
    <div className="bg-ink text-white rounded-[var(--kc-radius-md)] shadow-kc overflow-hidden" data-testid="audio-player">
      <div className="px-4 py-3 flex items-center gap-3">
        {/* Play / Pause */}
        {status !== 'playing' ? (
          <button
            onClick={handlePlay}
            className="h-11 w-11 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-[color:var(--kc-gold)] text-black hover:bg-[color:var(--kc-gold-2)] transition-colors"
            aria-label="Play narration"
            data-testid="audio-play-btn"
          >
            <Play size={18} fill="currentColor" />
          </button>
        ) : (
          <button
            onClick={handlePause}
            className="h-11 w-11 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-[color:var(--kc-gold)] text-black hover:bg-[color:var(--kc-gold-2)] transition-colors"
            aria-label="Pause narration"
            data-testid="audio-pause-btn"
          >
            <Pause size={18} fill="currentColor" />
          </button>
        )}

        {/* Stop */}
        <button
          onClick={handleStop}
          disabled={status === 'idle'}
          className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/20 text-white/80 hover:bg-white/10 transition-colors disabled:opacity-40"
          aria-label="Stop narration"
          data-testid="audio-stop-btn"
        >
          <Square size={14} fill="currentColor" />
        </button>

        {/* Title + progress */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Volume2 size={12} className="text-gold flex-shrink-0" />
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 truncate" data-testid="audio-title">
              {title}
            </div>
            <div className="ml-auto text-[11px] tabular-nums text-white/60" data-testid="audio-progress-pct">{pct}%</div>
          </div>
          <div className="mt-1.5 h-1 w-full bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-[color:var(--kc-gold)] transition-[width] duration-300"
              style={{ width: `${pct}%` }}
              data-testid="audio-progress-bar"
            />
          </div>
        </div>

        {/* Settings */}
        <button
          onClick={() => setShowSettings((s) => !s)}
          className="h-9 px-2.5 inline-flex items-center gap-1 rounded-full border border-white/20 text-white/80 hover:bg-white/10 transition-colors text-[11px] font-semibold"
          aria-label="Audio settings"
          data-testid="audio-settings-btn"
        >
          <Gauge size={12} /> {rate.toFixed(2)}x
          <ChevronDown size={12} className={`transition-transform ${showSettings ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Settings drawer */}
      {showSettings && (
        <div className="border-t border-white/10 px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-black/40">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-white/60 mb-1">Speed</label>
            <div className="flex flex-wrap gap-1.5" data-testid="audio-speed-options">
              {[0.75, 1, 1.25, 1.5, 1.75, 2].map((r) => (
                <button
                  key={r}
                  onClick={() => handleRateChange(r)}
                  className={`h-7 px-2.5 text-[11px] rounded-full border transition-colors ${
                    Math.abs(rate - r) < 0.01
                      ? 'bg-[color:var(--kc-gold)] text-black border-[color:var(--kc-gold)] font-semibold'
                      : 'border-white/20 text-white/80 hover:bg-white/10'
                  }`}
                  data-testid={`audio-speed-${r}`}
                >
                  {r}x
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-white/60 mb-1">Voice</label>
            <select
              value={voiceURI}
              onChange={(e) => handleVoiceChange(e.target.value)}
              className="w-full h-8 px-2 rounded-[var(--kc-radius-sm)] bg-black/40 border border-white/20 text-white text-xs focus:outline-none focus:ring-1 focus:ring-[color:var(--kc-gold)]"
              data-testid="audio-voice-select"
            >
              {voices.length === 0 && <option value="">Loading voices…</option>}
              {voices.slice(0, 24).map((v) => (
                <option key={v.voiceURI} value={v.voiceURI} className="bg-ink">
                  {v.name} {v.lang ? `(${v.lang})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 text-[10px] text-white/50 leading-relaxed">
            Powered by your device's built-in voices — free and private (text never leaves your browser). A premium Mickey-narrated voiceover is coming soon.
          </div>
        </div>
      )}
    </div>
  );
}
